import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import { GraduationCap, Plus, X, Trash2, CheckCircle2, Circle, Play, Upload, FolderPlus, ChevronLeft, Pencil, Search, GripVertical, Lock, ChevronUp, ChevronDown } from "lucide-react";
import { Linkify } from "@/components/Linkify";

const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
const lbl = "text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755] block mb-1";

const videoEmbed = (u) => {
  if (!u) return "";
  if (u.includes("drive.google.com")) {
    const d = u.match(/(?:file\/d\/|[?&]id=|uc\?id=)([\w-]{20,})/);
    if (d) return `https://drive.google.com/file/d/${d[1]}/preview`;
  }
  const m = u.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return u;
};

const courseProgress = (c) => {
  const mods = c.modules || [];
  let total = mods.length;
  let done = (c.completed_modules || []).length;
  const ck = new Set(c.completed_checklist || []);
  mods.forEach((m) => (m.checklist || []).forEach((it) => { total += 1; if (ck.has(it.id)) done += 1; }));
  return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
};

export default function Training() {
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.can_manage_users;
  const [courses, setCourses] = useState([]);
  const [folders, setFolders] = useState([]);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const [c, f] = await Promise.all([api.get("/training"), api.get("/training/folders")]);
    setCourses(c.data); setFolders(f.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveProgress = async (course, patch) => {
    const next = { completed_modules: course.completed_modules || [], completed_checklist: course.completed_checklist || [], ...patch };
    setCourses((cs) => cs.map((c) => (c.id === course.id ? { ...c, ...next } : c)));
    setActive((a) => (a && a.id === course.id ? { ...a, ...next } : a));
    await api.post(`/training/${course.id}/progress`, next).catch(() => toast.error("Couldn't save progress — check your connection."));
  };
  const toggleModule = (course, modId) => {
    const done = course.completed_modules || [];
    saveProgress(course, { completed_modules: done.includes(modId) ? done.filter((x) => x !== modId) : [...done, modId] });
  };
  const toggleChecklistItem = (course, itemId) => {
    const done = course.completed_checklist || [];
    saveProgress(course, { completed_checklist: done.includes(itemId) ? done.filter((x) => x !== itemId) : [...done, itemId] });
  };
  const del = async (id) => { if (!window.confirm("Delete this course?")) return; await api.delete(`/training/${id}`); toast.success("Deleted"); load(); };
  const addFolder = async () => {
    const name = window.prompt("New folder name"); if (!name) return;
    await api.post("/training/folders", { name }); load();
  };
  const renameFolder = async (folder) => {
    const name = window.prompt("Rename folder", folder.name); if (!name || name === folder.name) return;
    await api.put(`/training/folders/${folder.id}`, { name }); toast.success("Folder renamed"); load();
  };
  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?`)) return;
    try { await api.delete(`/training/folders/${folder.id}`); toast.success("Folder deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Move the courses out of this folder first."); }
  };
  const nameUncategorized = async () => {
    const name = window.prompt("Name this folder", "General"); if (!name) return;
    const { data } = await api.post("/training/folders", { name });
    const loose = courses.filter((c) => !c.folder_id);
    await Promise.all(loose.map((c) => api.put(`/training/${c.id}`, { ...c, folder_id: data.id })));
    toast.success("Folder created"); load();
  };

  const q = query.trim().toLowerCase();
  const matches = (c) => !q || `${c.title || ""} ${c.description || ""} ${(c.modules || []).map((m) => m.title).join(" ")}`.toLowerCase().includes(q);
  const grouped = [{ id: null, name: "Uncategorized" }, ...folders].map((f) => ({
    folder: f, items: courses.filter((c) => (c.folder_id || null) === f.id && matches(c)),
  })).filter((g) => (q ? g.items.length : (g.folder.id !== null || g.items.length)));
  const [dragCourse, setDragCourse] = useState(null);
  const canReorder = canManage && !q;
  const dropCourse = async (group, toId) => {
    const from = dragCourse; setDragCourse(null);
    if (!from || from.folder !== (group.folder.id || null) || from.id === toId) return;
    const ids = group.items.map((c) => c.id);
    const next = ids.filter((x) => x !== from.id);
    next.splice(ids.indexOf(toId), 0, from.id);
    const pos = Object.fromEntries(next.map((id, i) => [id, i]));
    const slots = group.items.map((c) => c.order ?? 0).sort((a, b) => a - b);
    setCourses((cs) => cs.map((c) => (c.id in pos ? { ...c, order: slots[pos[c.id]] } : c)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    try { await api.post("/training/reorder", { folder_id: group.folder.id, course_ids: next }); toast.success("Course order saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  if (active) return <CourseView course={active} onBack={() => setActive(null)} onToggle={toggleModule} onToggleChecklist={toggleChecklistItem} />;
  if (editing) return <CourseEditor course={editing} folders={folders} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div data-testid="training-page">
      <PageHeader title="Training" subtitle="Courses, videos and lessons for the team."
        action={canManage && (
          <div className="flex gap-2">
            <button data-testid="add-folder-btn" onClick={addFolder} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#E5E5E2] text-sm font-semibold hover:bg-[#F0EFEC]"><FolderPlus className="w-4 h-4" /> Folder</button>
            <button data-testid="new-course-btn" onClick={() => setEditing({ title: "", description: "", thumbnail_url: "", modules: [], folder_id: null })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312]"><Plus className="w-4 h-4" /> New Course</button>
          </div>
        )} />

      {courses.length === 0 && folders.length === 0 && <div className="text-center py-24 text-[#8a8a86]"><GraduationCap className="w-10 h-10 mx-auto mb-3 text-[#C9C9C4]" />No training courses yet.</div>}

      {courses.length > 0 && (
        <div className="relative mb-8 max-w-md">
          <Search className="w-4 h-4 text-[#8a8a86] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="training-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search training courses & lessons…"
            className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#E5E5E2] rounded-full text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none"
          />
        </div>
      )}

      {q && grouped.length === 0 && <div className="text-sm text-[#8a8a86]" data-testid="training-no-results">No courses match "{query}".</div>}

      {grouped.map((g) => (
        <div key={g.folder.id || "none"} className="mb-10">
          <div className="flex items-center gap-2 mb-4 group/folder">
            <h3 data-testid={`folder-title-${g.folder.id || "uncategorized"}`} className="text-sm font-semibold text-[#0A0A0A]">{g.folder.name}</h3>
            <span className="text-xs text-[#8a8a86]">{g.items.length}</span>
            {g.folder.restricted_roles?.length > 0 && (
              <span data-testid={`folder-restricted-${g.folder.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full bg-[#EEF2FB] text-[#1F3C88]"><Lock className="w-3 h-3" /> Admins & Contracts Team only</span>
            )}
            {canManage && g.folder.id !== null && (
              <span className="flex items-center gap-1 opacity-0 group-hover/folder:opacity-100 transition-opacity">
                <button data-testid={`folder-rename-${g.folder.id}`} onClick={() => renameFolder(g.folder)} className="text-[#8a8a86] hover:text-[#1F3C88]" title="Rename folder"><Pencil className="w-3.5 h-3.5" /></button>
                {!g.folder.restricted_roles?.length && <button data-testid={`folder-delete-${g.folder.id}`} onClick={() => deleteFolder(g.folder)} className="text-[#8a8a86] hover:text-[#992B2B]" title="Delete folder"><Trash2 className="w-3.5 h-3.5" /></button>}
              </span>
            )}
            {canManage && g.folder.id === null && (
              <button data-testid="folder-rename-uncategorized" onClick={nameUncategorized} className="text-[#8a8a86] hover:text-[#1F3C88] opacity-0 group-hover/folder:opacity-100 transition-opacity" title="Name this folder"><Pencil className="w-3.5 h-3.5" /></button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.items.map((c) => {
              const { total, done, pct } = courseProgress(c);
              return (
                <div key={c.id} data-testid={`course-card-${c.id}`}
                  onDragOver={(e) => { if (dragCourse && dragCourse.folder === (g.folder.id || null)) e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); dropCourse(g, c.id); }}
                  className={`bg-white border rounded-sm overflow-hidden group relative transition-opacity ${dragCourse?.id === c.id ? "opacity-40" : ""} ${dragCourse && dragCourse.id !== c.id && dragCourse.folder === (g.folder.id || null) ? "border-dashed border-[#1F3C88]" : "border-[#E5E5E2]"}`}>
                  {canReorder && g.items.length > 1 && (
                    <span draggable data-testid={`course-drag-${c.id}`} title="Drag to reorder courses"
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", c.id); setDragCourse({ id: c.id, folder: g.folder.id || null }); }}
                      onDragEnd={() => setDragCourse(null)}
                      className="absolute top-2 left-2 z-10 p-1.5 rounded-sm bg-white/90 shadow-sm cursor-grab"><GripVertical className="w-4 h-4 text-[#575755]" /></span>
                  )}
                  <button onClick={() => setActive(c)} className="block w-full text-left">
                    <div className="h-40 bg-[#F0EFEC] relative">
                      {c.thumbnail_url ? <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><GraduationCap className="w-8 h-8 text-[#C9C9C4]" /></div>}
                      <span className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/25 transition-colors"><Play className="w-9 h-9 text-white opacity-0 group-hover:opacity-100 transition-opacity" /></span>
                    </div>
                    <div className="p-4">
                      <div className="font-semibold text-[#0A0A0A]">{c.title}</div>
                      <p className="text-sm text-[#575755] mt-1 line-clamp-2">{c.description}</p>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-[#F0EFEC] overflow-hidden"><div className="h-full bg-[#1F8A4C]" style={{ width: `${pct}%` }} /></div>
                        <span className="text-xs text-[#8a8a86]">{done}/{total}</span>
                      </div>
                    </div>
                  </button>
                  {canManage && (
                    <div className="flex border-t border-[#E5E5E2]">
                      <button onClick={() => setEditing(c)} className="flex-1 py-2 text-xs font-semibold text-[#1F3C88] hover:bg-[#F8FAFE]">Edit</button>
                      <button onClick={() => del(c.id)} data-testid={`delete-course-${c.id}`} className="flex-1 py-2 text-xs font-semibold text-[#992B2B] hover:bg-[#F6E4E4] border-l border-[#E5E5E2]">Delete</button>
                    </div>
                  )}
                </div>
              );
            })}
            {g.items.length === 0 && <p className="text-sm text-[#8a8a86] col-span-full">No courses in this folder yet.</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function CourseView({ course, onBack, onToggle, onToggleChecklist }) {
  const mods = [...(course.modules || [])].sort((a, b) => a.order - b.order);
  const [sel, setSel] = useState(mods[0] || null);
  const done = course.completed_modules || [];
  return (
    <div data-testid="course-view">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] mb-6"><ChevronLeft className="w-4 h-4" /> All courses</button>
      <h1 className="font-serif text-3xl text-[#0A0A0A]">{course.title}</h1>
      <Linkify text={course.description} className="block text-[#575755] mt-2 mb-6 max-w-2xl" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {sel ? (
            <div className="bg-white border border-[#E5E5E2] rounded-sm overflow-hidden">
              {sel.video_url && (sel.video_type === "upload"
                ? <video src={sel.video_url} controls className="w-full aspect-video bg-black" />
                : <iframe title="video" src={videoEmbed(sel.video_url)} className="w-full aspect-video" allow="autoplay; encrypted-media" allowFullScreen />)}
              <div className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-lg text-[#0A0A0A]">{sel.title}</h3>
                  <button data-testid="toggle-complete-btn" onClick={() => onToggle(course, sel.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${done.includes(sel.id) ? "bg-[#E7F0EA] text-[#2E5C47]" : "border border-[#E5E5E2] text-[#575755]"}`}>
                    {done.includes(sel.id) ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />} {done.includes(sel.id) ? "Completed" : "Mark complete"}
                  </button>
                </div>
                {sel.content && <Linkify text={sel.content} className="block text-sm text-[#575755] mt-3" />}
                {sel.checklist && sel.checklist.length > 0 && (
                  <ul className="mt-4 space-y-2.5" data-testid="lesson-checklist">
                    {sel.checklist.map((it) => {
                      const checked = (course.completed_checklist || []).includes(it.id);
                      return (
                        <li key={it.id} className="flex items-start gap-3">
                          <button onClick={() => onToggleChecklist(course, it.id)} data-testid={`checklist-item-${it.id}`} className="mt-0.5 shrink-0" aria-label="Toggle item">
                            {checked ? <CheckCircle2 className="w-5 h-5 text-[#1F8A4C]" /> : <Circle className="w-5 h-5 text-[#C9C9C4] hover:text-[#1F3C88] transition-colors" />}
                          </button>
                          {it.url
                            ? <a href={it.url} target="_blank" rel="noopener noreferrer" className={`text-sm break-words ${checked ? "line-through text-[#8a8a86]" : "text-[#1F3C88] underline underline-offset-2 hover:text-[#F7941D]"}`}>{it.text}</a>
                            : <span className={`text-sm ${checked ? "line-through text-[#8a8a86]" : "text-[#0A0A0A]"}`}>{it.text}</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ) : <div className="text-[#8a8a86]">This course has no lessons yet.</div>}
        </div>
        <div className="space-y-2">
          {mods.map((m, i) => (
            <button key={m.id} onClick={() => setSel(m)} className={`w-full text-left flex items-center gap-3 p-3 rounded-sm border ${sel && sel.id === m.id ? "border-[#F7941D] bg-[#FEF3E7]" : "border-[#E5E5E2] hover:bg-[#F8FAFE]"}`}>
              {done.includes(m.id) ? <CheckCircle2 className="w-4 h-4 text-[#1F8A4C] shrink-0" /> : <Circle className="w-4 h-4 text-[#C9C9C4] shrink-0" />}
              <span className="text-sm text-[#0A0A0A]">{i + 1}. {m.title || "Untitled lesson"}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CourseEditor({ course, folders, onClose, onSaved }) {
  const [form, setForm] = useState({ ...course, modules: course.modules || [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addModule = () => set("modules", [...form.modules, { title: "", content: "", video_url: "", video_type: "youtube", checklist: [], order: form.modules.length }]);
  const setMod = (i, k, v) => setForm((f) => ({ ...f, modules: f.modules.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)) }));
  const rmMod = (i) => set("modules", form.modules.filter((_, idx) => idx !== i));
  const addChecklistItem = (i) => setForm((f) => ({ ...f, modules: f.modules.map((m, idx) => (idx === i ? { ...m, checklist: [...(m.checklist || []), { text: "", url: "" }] } : m)) }));
  const setChecklistItem = (i, j, k, v) => setForm((f) => ({ ...f, modules: f.modules.map((m, idx) => (idx === i ? { ...m, checklist: (m.checklist || []).map((it, jdx) => (jdx === j ? { ...it, [k]: v } : it)) } : m)) }));
  const rmChecklistItem = (i, j) => setForm((f) => ({ ...f, modules: f.modules.map((m, idx) => (idx === i ? { ...m, checklist: (m.checklist || []).filter((_, jdx) => jdx !== j) } : m)) }));
  const [dragCk, setDragCk] = useState(null);
  const [dragMod, setDragMod] = useState(null);
  const moveMod = (from, to) => setForm((f) => {
    if (to < 0 || to >= f.modules.length || from === to) return f;
    const arr = [...f.modules];
    const [mv] = arr.splice(from, 1); arr.splice(to, 0, mv);
    return { ...f, modules: arr };
  });
  const moveChecklistItem = (i, from, to) => setForm((f) => ({ ...f, modules: f.modules.map((m, idx) => {
    if (idx !== i) return m;
    const arr = [...(m.checklist || [])];
    const [mv] = arr.splice(from, 1); arr.splice(to, 0, mv);
    return { ...m, checklist: arr };
  }) }));

  const uploadThumb = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const d = await uploadFile(f, "/training/upload"); set("thumbnail_url", d.url); toast.success("Thumbnail uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };
  const uploadVideo = async (i, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const d = await uploadFile(f, "/training/upload"); setMod(i, "video_url", d.url); setMod(i, "video_type", "upload"); toast.success("Video uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/training/${form.id}`, form);
      else await api.post("/training", form);
      toast.success(form.id ? "Course saved" : "Course created — agents notified");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="course-editor" className="max-w-3xl">
      <button onClick={onClose} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] mb-6"><ChevronLeft className="w-4 h-4" /> Back</button>
      <h1 className="font-serif text-3xl text-[#0A0A0A] mb-6">{form.id ? "Edit Course" : "New Course"}</h1>
      <div className="space-y-4 bg-white border border-[#E5E5E2] rounded-sm p-5">
        <div><span className={lbl}>Title</span><input data-testid="course-title" className={inp} value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
        <div><span className={lbl}>Description</span><textarea data-testid="course-description" rows={3} className={inp} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <span className={lbl}>Folder</span>
            <select className={inp} value={form.folder_id || ""} onChange={(e) => set("folder_id", e.target.value || null)}>
              <option value="">Uncategorized</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <span className={lbl}>Thumbnail</span>
            <div className="flex items-center gap-2">
              {form.thumbnail_url && <img src={form.thumbnail_url} alt="" className="w-12 h-12 rounded object-cover" />}
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#E5E5E2] text-sm cursor-pointer hover:bg-[#F0EFEC]"><Upload className="w-4 h-4" /> Upload<input type="file" accept="image/*" hidden onChange={uploadThumb} /></label>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 mb-3">
        <h3 className="text-sm font-semibold">Lessons</h3>
        <button onClick={addModule} data-testid="add-lesson-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E5E2] text-sm font-semibold hover:bg-[#F0EFEC]"><Plus className="w-4 h-4" /> Lesson</button>
      </div>
      <div className="space-y-4">
        {form.modules.map((m, i) => (
          <div key={m.id || `new-${i}`} data-testid={`lesson-card-${i}`}
            onDragOver={(e) => { if (dragMod !== null) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); if (dragMod !== null) moveMod(dragMod, i); setDragMod(null); }}
            className={`bg-white border rounded-sm p-4 transition-colors ${dragMod !== null && dragMod !== i ? "border-dashed border-[#C9C9C4]" : "border-[#E5E5E2]"} ${dragMod === i ? "opacity-50" : ""}`}>
            <div className="flex items-center gap-2 mb-3">
              <span draggable onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", `lesson-${i}`); setDragMod(i); }} onDragEnd={() => setDragMod(null)} data-testid={`lesson-drag-${i}`} className="cursor-grab shrink-0" title="Drag to reorder lessons"><GripVertical className="w-4 h-4 text-[#C9C9C4]" /></span>
              <span className="text-xs font-semibold text-[#8a8a86] w-5 shrink-0">{i + 1}.</span>
              <input className={inp} placeholder="Lesson title" value={m.title} onChange={(e) => setMod(i, "title", e.target.value)} />
              <div className="flex flex-col shrink-0">
                <button type="button" data-testid={`lesson-up-${i}`} disabled={i === 0} onClick={() => moveMod(i, i - 1)} className="text-[#8a8a86] hover:text-[#1F3C88] disabled:opacity-25" title="Move up"><ChevronUp className="w-4 h-4" /></button>
                <button type="button" data-testid={`lesson-down-${i}`} disabled={i === form.modules.length - 1} onClick={() => moveMod(i, i + 1)} className="text-[#8a8a86] hover:text-[#1F3C88] disabled:opacity-25" title="Move down"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <button onClick={() => rmMod(i)} data-testid={`lesson-delete-${i}`} className="text-[#8a8a86] hover:text-[#992B2B]"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-2 mb-3">
              <input className={inp} placeholder="YouTube, Vimeo or Google Drive link" value={m.video_type === "upload" ? "" : m.video_url} onChange={(e) => setForm((f) => ({ ...f, modules: f.modules.map((mm, idx) => (idx === i ? { ...mm, video_url: e.target.value, video_type: "youtube" } : mm)) }))} disabled={m.video_type === "upload"} />
              <label className="shrink-0 inline-flex items-center gap-1.5 px-3 rounded-sm border border-[#E5E5E2] text-sm cursor-pointer hover:bg-[#F0EFEC]"><Upload className="w-4 h-4" /> Video<input type="file" accept="video/*" hidden onChange={(e) => uploadVideo(i, e)} /></label>
            </div>
            {m.video_type === "upload" && m.video_url && <p className="text-xs text-[#1F8A4C] mb-2">Uploaded video attached · <button className="underline" onClick={() => { setMod(i, "video_url", ""); setMod(i, "video_type", "youtube"); }}>remove</button></p>}
            <textarea rows={2} className={inp} placeholder="Lesson notes (optional)" value={m.content} onChange={(e) => setMod(i, "content", e.target.value)} />
            <div className="mt-3 pt-3 border-t border-[#F0EFEC]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755]">Checklist (optional)</span>
                <button type="button" onClick={() => addChecklistItem(i)} data-testid={`add-checklist-item-${i}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1F3C88] hover:underline"><Plus className="w-3.5 h-3.5" /> Item</button>
              </div>
              <div className="space-y-2">
                {(m.checklist || []).map((it, j) => (
                  <div key={j} className="flex items-center gap-2"
                    onDragOver={(e) => { if (dragCk && dragCk.i === i) e.preventDefault(); }}
                    onDrop={() => { if (dragCk && dragCk.i === i && dragCk.j !== j) moveChecklistItem(i, dragCk.j, j); setDragCk(null); }}>
                    <span draggable onDragStart={() => setDragCk({ i, j })} data-testid={`checklist-drag-${i}-${j}`} className="cursor-grab shrink-0" title="Drag to reorder"><GripVertical className="w-4 h-4 text-[#C9C9C4]" /></span>
                    <Circle className="w-4 h-4 text-[#C9C9C4] shrink-0" />
                    <input className={inp} placeholder="Item text" value={it.text} onChange={(e) => setChecklistItem(i, j, "text", e.target.value)} />
                    <input className={inp} placeholder="Link (optional)" value={it.url} onChange={(e) => setChecklistItem(i, j, "url", e.target.value)} />
                    <button type="button" onClick={() => rmChecklistItem(i, j)} className="text-[#8a8a86] hover:text-[#992B2B] shrink-0"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {(m.checklist || []).length === 0 && <p className="text-xs text-[#8a8a86]">Add checklist items (e.g. links to complete). Leave empty for a video/notes-only lesson.</p>}
              </div>
            </div>
          </div>
        ))}
        {form.modules.length === 0 && <p className="text-sm text-[#8a8a86]">No lessons yet — add one above.</p>}
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="save-course-btn" className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] disabled:opacity-60">{saving ? "Saving…" : (form.id ? "Save Course" : "Create Course")}</button>
      </div>
    </div>
  );
}
